import {inject} from "vue";
import {useI18n} from "vue-i18n";
import {useRoute} from "vue-router";
import {useGlobalTabs} from "@/composables/useGlobalTabs";
import {
  OF_STATUS,
  GLOBAL_TAB_SCHEDULING_PLANNING_CALENDAR,
  GLOBAL_TAB_SCHEDULING_PLANNING_CAPACITY,
  GLOBAL_TAB_SCHEDULING_PLANNING_OFS,
  GLOBAL_TAB_SCHEDULING_COMPONENTS,
  GLOBAL_TAB_SCHEDULING_PILOTING_IN_PROGRESS,
  GLOBAL_TAB_SCHEDULING_PILOTING_PROGRESS_TRACKING,
} from "@/config/constants";
import {OpenDialogFunction, Segment} from "@/interfaces";

export enum TrackOptionsCtaOrigin {
  Card = "card",
  Modal = "modal",
  Sidebar = "sidebar",
}

type TrackOptionsPageOrigin =
  | typeof GLOBAL_TAB_SCHEDULING_PLANNING_CALENDAR
  | typeof GLOBAL_TAB_SCHEDULING_PLANNING_CAPACITY
  | typeof GLOBAL_TAB_SCHEDULING_PLANNING_OFS
  | typeof GLOBAL_TAB_SCHEDULING_COMPONENTS
  | typeof GLOBAL_TAB_SCHEDULING_PILOTING_IN_PROGRESS
  | typeof GLOBAL_TAB_SCHEDULING_PILOTING_PROGRESS_TRACKING;

export function useSchedulingOperationStatusChange(params?: {
  trackOptions: {
    cta_origin: TrackOptionsCtaOrigin;
  };
}) {
  const openDialog = inject<OpenDialogFunction>("openDialog");
  const segment = inject<Segment>("segment");

  const route = useRoute();
  const {t} = useI18n();
  const {globalTabsPerRoute, globallySelectedTab} = useGlobalTabs();

  const {trackOptions = {}} = params || {};

  async function handleOperationsStatusChangeConfirmation({
    update,
    operations,
  }: {
    update: {new_status?: string};
    operations: {op_status?: string; op_name?: string; of_id?: string}[];
  }): Promise<boolean> {
    const {new_status} = update;

    const operationsNotOngoing = operations.filter(
      ({op_status}) => op_status !== OF_STATUS.ON_GOING,
    );

    let hasConfirmed = true;
    let modalAction = "none";

    if (new_status === OF_STATUS.DONE && operationsNotOngoing.length > 0) {
      const [{op_name, of_id}] = operationsNotOngoing;

      hasConfirmed = await openDialog({
        header: t("scheduling.status_update__header"),
        message: t("scheduling.status_update__body", {op_name, of_id}),
        validateText: t("scheduling.status_update__confirm"),
        type: "warning",
        hidePrefix: true,
        hideIcon: true,
        disableHeaderColor: true,
        confirmBtnProps: {
          filled: false,
          outlined: true,
        },
        action: () => {
          modalAction = "confirm";
        },
        onCancelClicked: () => {
          modalAction = "cancel";
        },
      });
    }

    const page_origin = globalTabsPerRoute.value[route?.name as string]?.[
      globallySelectedTab.value
    ] as TrackOptionsPageOrigin;

    for (const {op_status, of_id} of operations) {
      segment.value.track("[Ordo] Operation Status Updated", {
        ...trackOptions,
        page_origin,
        modal_action: modalAction,
        old_status: op_status,
        new_status,
        of_id,
      });
    }

    return hasConfirmed;
  }

  return {
    handleOperationsStatusChangeConfirmation,
  };
}
